// apps/codeframe/src/chrome/timeline/EasingPopover.tsx
import { useTimelineStore } from './timeline-store';
import { useCanvasStore } from '../../store/canvas-store';
import type { MotionTimeline, EaseSpec, Keyframe, MotionProp, MotionTrack } from '../../store/types';

const PRESETS: { label: string; ease: EaseSpec }[] = [
  { label: 'linear', ease: 'linear' },
  { label: 'easeIn', ease: 'easeIn' },
  { label: 'easeOut', ease: 'easeOut' },
  { label: 'easeInOut', ease: 'easeInOut' },
  { label: 'step', ease: 'step' },
  { label: 'spring', ease: { spring: { stiffness: 180, damping: 12 } } },
];

function findKf(
  timeline: MotionTimeline,
  trackId: string,
  layerId: string,
  prop: MotionProp,
  t: number,
): Keyframe | undefined {
  const track: MotionTrack | undefined = timeline.tracks.find((tr) => tr.id === trackId);
  const layer = track?.layers.find((l) => l.id === layerId);
  const ch = layer?.channels.find((c) => c.prop === prop);
  return ch?.keyframes.find((k) => k.t === t);
}

export function EasingPopover({ frameId, timeline }: { frameId: string; timeline: MotionTimeline }) {
  const easingFor = useTimelineStore((s) => s.easingFor);
  const closeEasing = useTimelineStore((s) => s.closeEasing);
  const dispatch = useCanvasStore((s) => s.dispatch);

  if (!easingFor) return null;
  const { ref, x, y } = easingFor;
  const prop = ref.prop as MotionProp;
  const kf = findKf(timeline, ref.trackId, ref.layerId, prop, ref.t);
  if (!kf) return null;

  const setEase = (ease: EaseSpec) =>
    dispatch({ t: 'motion.setEase', frameId, trackId: ref.trackId, layerId: ref.layerId, prop, kfT: ref.t, ease });

  const setValue = (raw: string) => {
    const isColorProp = prop === 'color' || prop === 'backgroundColor';
    const value: number | string = isColorProp ? raw : Number(raw);
    if (!isColorProp && Number.isNaN(value as number)) return;
    dispatch({
      t: 'motion.setKeyframe',
      frameId,
      trackId: ref.trackId,
      layerId: ref.layerId,
      prop,
      kf: { t: ref.t, value, ease: kf.ease },
    });
  };

  const remove = () => {
    dispatch({ t: 'motion.removeKeyframe', frameId, trackId: ref.trackId, layerId: ref.layerId, prop, atT: ref.t });
    closeEasing();
  };

  const isColor = prop === 'color' || prop === 'backgroundColor';
  const curEaseLabel = typeof kf.ease === 'string' ? kf.ease : Object.keys(kf.ease)[0];

  return (
    <>
      <div className="fixed inset-0 z-40" onMouseDown={closeEasing} />
      <div
        className="fixed z-50 w-56 rounded-md border border-[var(--color-chip-border)] bg-[var(--color-surface)] p-2 text-xs text-[var(--color-text)] shadow-[var(--shadow-2)] font-[family-name:var(--font-sans)]"
        style={{ left: Math.min(x, window.innerWidth - 240), top: Math.max(8, y - 200) }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex items-center justify-between text-[var(--color-text-muted)] font-[family-name:var(--font-mono)]">
          <span>
            Keyframe · {prop} @ {(ref.t / 1000).toFixed(2)}s
          </span>
          <button onClick={remove} className="hover:text-[var(--color-danger)]">
            remove
          </button>
        </div>

        <label className="mb-2 block">
          value
          <input
            type={isColor ? 'color' : 'number'}
            defaultValue={isColor && String(kf.value).includes('var(') ? '#000000' : String(kf.value)}
            key={String(kf.value)}
            onBlur={(e) => setValue(e.target.value)}
            className="ml-2 w-28 rounded border border-[var(--color-chip-border)] bg-[var(--color-surface)] px-1 py-0.5"
          />
        </label>
        {isColor && (
          <label className="mb-2 block text-[var(--color-text-muted)]">
            token
            <input
              type="text"
              placeholder="var(--color-accent)"
              defaultValue={String(kf.value).includes('var(') ? String(kf.value) : ''}
              key={`tok-${String(kf.value)}`}
              onBlur={(e) => e.target.value.trim() && setValue(e.target.value.trim())}
              className="ml-2 w-40 rounded border border-[var(--color-chip-border)] bg-[var(--color-surface)] px-1 py-0.5 font-[family-name:var(--font-mono)]"
              title="A theme token follows light/dark, e.g. var(--color-accent)"
            />
          </label>
        )}

        <div className="mb-1 text-[var(--color-text-muted)]">ease ({curEaseLabel})</div>
        <div className="grid grid-cols-3 gap-1">
          {PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => setEase(p.ease)}
              className={`rounded border px-1 py-0.5 ${
                curEaseLabel === p.label
                  ? 'border-[var(--color-accent)] text-[var(--color-accent)]'
                  : 'border-[var(--color-chip-border)]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
